import { Box } from '@mui/material';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import PageContainer from 'Components/PageContainer';
import PageSubTitle from 'Components/PageSubTitle';
import PageTitle from 'Components/PageTitle';
import ScrollableTableContainer from 'Components/ScrollableTableContainer';
import { PARTNER_REQUEST_ROWS_PER_PAGE } from 'utils/pagination.utils';
import { EmptyStateCard, ThemeButton } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import DocumentSkeleton from 'FeatureModules/Document/Components/DocumentSkeleton';
import PaginatedRequestsTable from 'FeatureModules/Request/Components/Tables/PaginatedRequestsTable';
import useRequests from 'FeatureModules/Request/hooks/useRequests';
import React, { FC, Fragment, PropsWithChildren } from 'react';
import { EBackgroundTypes, EItemTypes } from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';

const PartnerRequests: FC<PropsWithChildren> = () => {
  const {
    allRequests,
    filteredRequests,
    fulfilledRequests,
    loading,
    error,
    partner,
    searchTerm,
    isPartnerRegistered,
    setSearchTerm,
    handleRequestClick,
    handleActionClick,
    handleNewRequestClick,
  } = useRequests();

  if (error) {
    return <ErrorState />;
  }
  if (loading) {
    return <Loader />;
  }
  if (!allRequests.length && !searchTerm) {
    return (
      <PageContainer>
        <PageTitle
          title="Partner requests"
          titleExtension={partner?.name}
          goBackLabel="All partners"
          goBackUrl="/partners"
        />
        <Box mt={4}>
          <EmptyStateCard
            title={
              isPartnerRegistered
                ? 'Request partner information to manage and prove your impact'
                : 'Your partner needs to be registered on the platform to request information'
            }
            onCreateItemClick={handleNewRequestClick}
            variant={EItemTypes.DOCUMENT}
            buttonText={isPartnerRegistered ? 'Add request' : 'Re-send invite'}
            buttonIcon={
              isPartnerRegistered ? (
                <Icon name="document-request-add" color="white" />
              ) : (
                <Icon name="send" color="white" />
              )
            }
            background={EBackgroundTypes.DETAILED_DOCUMENTS}
            size={EAvailableSizes.MEDIUM}
            data-testid="partner-requests-getting-started-card"
            data-testid-button="new-request-getting-started-card-btn"
          />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle title="Partner requests" goBackLabel="All partners" goBackUrl="/partners" />

      <Fragment>
        <Box position="relative">
          <ActionContainer>
            <SearchToolbar
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
              data-testid="document-search-input"
              placeholder="Search document"
              className="search-input"
            />
            <Box mr={1} />
            <ThemeButton
              data-testid="new-request-btn"
              startIcon={<Icon name="document-request-pending" color="white" />}
              onClick={handleNewRequestClick}
            >
              New request
            </ThemeButton>
          </ActionContainer>

          <ScrollableTableContainer>
            {filteredRequests.length ? (
              <PaginatedRequestsTable
                rowsPerPage={PARTNER_REQUEST_ROWS_PER_PAGE}
                requests={filteredRequests}
                onRequestClick={handleRequestClick}
                onActionClick={handleActionClick}
              />
            ) : (
              <Fragment />
            )}

            {!searchTerm && fulfilledRequests.length ? (
              <Fragment>
                {filteredRequests.length ? (
                  <PageSubTitle title="Completed requests" />
                ) : (
                  <Fragment />
                )}
                <PaginatedRequestsTable
                  data-testid="fulfilled-requests-table-body"
                  rowsPerPage={PARTNER_REQUEST_ROWS_PER_PAGE}
                  hideHeader={!!filteredRequests.length}
                  requests={fulfilledRequests}
                  onRequestClick={handleRequestClick}
                  onActionClick={handleActionClick}
                />
              </Fragment>
            ) : (
              <Fragment />
            )}
          </ScrollableTableContainer>
        </Box>

        {searchTerm.length && filteredRequests.length === 0 ? (
          <EmptySearchResults
            message="No search results found"
            skeletonComponent={<DocumentSkeleton title={searchTerm} />}
          />
        ) : (
          <Fragment />
        )}
      </Fragment>
    </PageContainer>
  );
};

export default PartnerRequests;
